import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AlertCircle, UserRound } from "lucide-react";

import { AuthPanel, AuthPending, AuthScreen, Button } from "@h2j-data-team/ui";

import { BrandLogo } from "@/components/brand-logo";
import { getSsoStatus } from "@/lib/api/sso";
import { authClient } from "@/lib/auth-client";
import { H2J_PROVIDER_ID, sanitizeRedirect, signInWithH2j } from "@/lib/sso";
import { PRODUCT_NAME } from "@repo/shared";

/**
 * The `launch_url` of the Login H2J tile (#700): it always goes through the Login
 * H2J sign-in, which answers without asking for a password when the person already
 * signed in there. The callback signs in whoever Login H2J says it is and ends the
 * session this browser had (#704), so another person's session never carries over.
 *
 * Exception: after the switch (`ssoOnly`), a session of an account not linked to
 * Login H2J is an emergency account. It is not swapped without asking: the person
 * chooses between Login H2J and staying in the emergency account.
 */
export const Route = createFileRoute("/auth/sso")({
  validateSearch: (search: Record<string, unknown>) => ({
    return_to: sanitizeRedirect(
      typeof search.return_to === "string" ? search.return_to : undefined,
    ),
  }),
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data) return { emergencyEmail: null };
    const [status, accounts] = await Promise.all([
      getSsoStatus().catch(() => null),
      authClient.listAccounts(),
    ]);
    const linked = accounts.data?.some((account) => account.providerId === H2J_PROVIDER_ID);
    return { emergencyEmail: status?.ssoOnly && !linked ? session.data.user.email : null };
  },
  head: () => ({ meta: [{ title: `Sign in — ${PRODUCT_NAME}` }] }),
  component: SsoStartPage,
});

function SsoStartPage() {
  const { t } = useTranslation("auth");
  const { return_to: returnTo } = Route.useSearch();
  const { emergencyEmail } = Route.useRouteContext();
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(!emergencyEmail);
  const [failed, setFailed] = useState(false);
  // One start only: StrictMode mounts twice, and a second `state` would replace
  // the cookie of the first one halfway.
  const started = useRef(false);

  useEffect(() => {
    if (!confirmed || started.current) return;
    started.current = true;
    signInWithH2j(returnTo ?? "/dashboard").catch(() => setFailed(true));
  }, [confirmed, returnTo]);

  return (
    <AuthScreen logo={<BrandLogo className="h-8 w-auto" />}>
      {!confirmed ? (
        <AuthPanel
          key="emergency"
          tone="warning"
          icon={<UserRound />}
          title={t("sso.emergencySession.title")}
          description={t("sso.emergencySession.description", { email: emergencyEmail })}
          actions={
            <>
              <Button size="lg" onClick={() => setConfirmed(true)}>
                {t("sso.emergencySession.switch")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate({ href: returnTo ?? "/dashboard" })}
              >
                {t("sso.emergencySession.stay")}
              </Button>
            </>
          }
        />
      ) : failed ? (
        <AuthPanel
          key="failed"
          tone="danger"
          icon={<AlertCircle />}
          title={t("sso.startFailed")}
          actions={
            <Button size="lg" variant="outline" asChild>
              <Link
                to="/login"
                search={{
                  mode: undefined,
                  token: undefined,
                  error: undefined,
                  redirect: returnTo,
                  email: undefined,
                }}
              >
                {t("backToSignIn")}
              </Link>
            </Button>
          }
        />
      ) : (
        <AuthPending label={t("sso.redirecting")} />
      )}
    </AuthScreen>
  );
}
